import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import { useChat } from "../../../context/chatContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import { useCases } from "../../../utils/constants";
import {
	get_questions_by_flow_id,
	question_flows,
	get_framework_options,
	framework_domains,
} from "../../../utils/endpoints";
import { Question, QuestionFlow } from "../../../utils/interfaces";
import Alert from "../../common/alerts/alert";
import ChatHeader from "../../common/chat/chatHeader";
import ChatHistory from "../../common/chatHistory";
import Footer from "../../common/footer";
import Questions from "../../common/maturityAssessment/questions";

const MaturityQuestionnaire = () => {
	const { startChat, resetChatState, setSelectedUseCase} = useChat();
	const navigate = useNavigate();
	const { domainId, questionFlowId } = useParams();
	const { logout } = useAuth();
	const [isSelecting, setIsSelecting] = useState(false);
	const [selectedFlow, setSelectedFlow] = useState<QuestionFlow>();
	const [questions, setQuestions] = useState<Question[]>([]);
	const [frameworkId, setFrameworkId] = useState<number | null>(null);

	const [alertShow, setAlertShow] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<string>("");
	const [alertType, setAlertType] = useState<
		| "primary"
		| "secondary"
		| "success"
		| "danger"
		| "warning"
		| "info"
		| "light"
		| "dark"
	>("danger");

	const handleSelect = async (index: number) => {
		if (isSelecting) return;
		resetChatState();
		// Set the selected use case
		setSelectedUseCase(index);
		setIsSelecting(true);
		// below two sets the questions and alert to default values before navigating away.
		setQuestions([]);
		setAlertShow(false);

		if (index === useCases[0].value) {
			navigate(useCases[0].route);
		} else if (index === useCases[1].value) {
			navigate(useCases[1].route);
		} else if (index === useCases[2].value) {
			navigate(useCases[2].route);
		} else if (index === useCases[3].value) {
			navigate(useCases[3].route);
		}
		setIsSelecting(false);
	};

	const handleQuit = () => {
		setQuestions([]);
		setAlertShow(false);
		navigate(-1);
	};

	const handleAlert = ({
		message,
		show = true,
		type = "danger",
	}: {
		message: string;
		show: boolean;
		type:
			| "primary"
			| "secondary"
			| "success"
			| "danger"
			| "warning"
			| "info"
			| "light"
			| "dark";
	}) => {
		setAlertMessage(message);
		setAlertShow(show);
		setAlertType(type);
	};

	const fetchQuestionsFlow = async () => {
		if (!domainId) return;

		try {
			// First fetch the domain to get its category_id (framework_id)
			const domainResponse = await createAxiosInstance().get(framework_domains(Number(domainId)));
			if (domainResponse.status === 200) {
				const domain = domainResponse.data;
				if (domain?.category_id) {
					setFrameworkId(domain.category_id);
				}
			}

			const questionsFlowResponse = await createAxiosInstance().get(
				question_flows(Number(domainId))
			);

			if (
				questionsFlowResponse.status == 401 ||
				questionsFlowResponse.status == 403
			) {
				// 401 is for expired token
				// 403 is for forbidden access (blocked by GPT.)
				console.log("user token expired");
				logout();
				navigate("/");
				return;
			}

			// Loop through the question flows and select the one that matches the questionFlowId
			const flows = questionsFlowResponse.data;
			const matchingFlow = flows.find(
				(flow: QuestionFlow) => flow.id === Number(questionFlowId)
			);
			setSelectedFlow(matchingFlow);
		} catch (e: any) {
			console.log(e);
		}
	};

	useEffect(() => {
		const getQuestions = async () => {
			if (!questionFlowId) return;

			try {
				const response = await createAxiosInstance().get(
					get_questions_by_flow_id(Number(questionFlowId))
				);
				if (response.status == 401 || response.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("user token expired");
					logout();
					return;
				}

				setQuestions(response.data);
			} catch (e: any) {
				if (e.response.status == 402) {
					console.log(e.response.data.detail);
					handleQuit();
					setAlertShow(true);
					setAlertMessage(e.response.data.detail);
					setAlertType("danger");
					return;
				}
				console.log(e);
			}
		};

		getQuestions();
		fetchQuestionsFlow();
	}, [questionFlowId]);

	return (
		<>
			<ChatHeader
				useCase={useCases[useCases.length - 1].value}
				selectUseCase={handleSelect}
			/>
			<main className="d-flex flex-column min-vh-65">
				<div
					className="authentication card-main h-100 align-self-center"
					style={{ marginTop: "2%" }}
				>
					{questionFlowId && selectedFlow && frameworkId && (
						<Questions
							selectedFlow={selectedFlow}
							questions={questions}
							handleQuit={handleQuit}
							frameworkId={frameworkId}
							handleAlert={(alert: { message: any; show: any; type: any }) =>
								handleAlert({
									message: alert.message,
									show: alert.show,
									type: alert.type,
								})
							}
						/>
					)}
				</div>
				<Footer />
			</main>
			<ChatHistory
				onNavigate={(path: string) => {
					navigate(path);
				}}
			/>
			<Alert
				message={alertMessage}
				show={alertShow}
				onClose={() => setAlertShow(false)}
				type={alertType}
			/>
		</>
	);
};

export default MaturityQuestionnaire;
